import { deferred } from '~/utils/async'
import { AppEvents, globalEventEmitter, sleep } from '~/utils/shared'
import { getDeviceId } from '~/utils/storage'
import { isPublicPath } from '~/utils/utils'
import { trace } from './analyticsService'

export enum SocketAction {
  OrganizationIndexes = 'organization.indexes'
}

export enum SocketEvent {
  ConversationUpdated = 'ConversationUpdated',
  AgentStatusUpdated = 'AgentStatusUpdated'
}

export class Socket {
  private static _socket: WebSocket | null = null
  private static _deferredValues: Map<string, any> = new Map()
  private static MAX_RETRIES = 100
  private static retryCount = 0

  public static connect() {
    const { pathname } = new URL(location.href)
    const NON_WEBSOCKET_PAGES = ['/human-annotations', '/super-admin']
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${protocol}://${location.host}/api/ws${isPublicPath() ? '/public' : ''}`
    const query = `?device_id=${getDeviceId()}`

    if (NON_WEBSOCKET_PAGES.includes(pathname)) {
      return
    }

    Socket._socket = new WebSocket(url + query)
    Socket._addListeners(Socket._socket)
  }

  public static reconnect(): void {
    if (Socket._socket) {
      if (Socket.isConnected()) {
        Socket._socket.close()
      }
      Socket._removeListeners(Socket._socket)
      Socket._socket = null
    }
    Socket.connect()
  }

  private static _onOpen() {
    trace('ws_open')
  }

  private static _onMessage(e: MessageEvent) {
    const data = JSON.parse(e.data)
    const { request_id, data: payload, error, event } = data
    const deferredValue = Socket._deferredValues.get(request_id)

    try {
      if (event === SocketEvent.ConversationUpdated) {
        trace({
          event: 'ws_message',
          labels: {
            message: e.data
          }
        })
      }
    } catch (_e) {}

    if (deferredValue) {
      if (error) {
        return deferredValue.reject(error)
      }
      return deferredValue.resolve(payload)
    } else {
      globalEventEmitter.emit(AppEvents.SocketEvent, data)
    }
  }

  private static async _onClose(event: CloseEvent) {
    try {
      const { code, reason } = event
      console.error('Socket closed. Attempting to reconnect.', { code, reason })
      await sleep(1e3)
      Socket.retryCount++
      if (
        Socket._socket &&
        (Socket._socket.readyState === Socket._socket.CONNECTING ||
          Socket._socket.readyState === Socket._socket.OPEN)
      ) {
        Socket.retryCount = 0
        return
      }
      if (Socket.retryCount > Socket.MAX_RETRIES) {
        console.info(
          '[HMR] Unable to establish a connection after exceeding the maximum retry attempts.'
        )
        Socket.retryCount = 0
        return
      }
      Socket.reconnect()
    } finally {
      trace({
        event: 'ws_close',
        labels: {
          code: String(event.code),
          reason: event.reason
        }
      })
    }
  }

  private static _onError(e: Event): void {
    trace({
      event: 'ws_error',
      labels: {
        error: e.toString()
      }
    })
  }

  private static _addListeners(socket: WebSocket) {
    socket.addEventListener('open', Socket._onOpen)
    socket.addEventListener('message', Socket._onMessage)
    socket.addEventListener('close', Socket._onClose)
    socket.addEventListener('error', Socket._onError)
  }

  private static _removeListeners(socket: WebSocket) {
    socket.removeEventListener('open', Socket._onOpen)
    socket.removeEventListener('message', Socket._onMessage)
    socket.removeEventListener('close', Socket._onClose)
    socket.removeEventListener('error', Socket._onError)
  }

  public static isConnected(): boolean {
    return Socket._socket !== null && Socket._socket.readyState === WebSocket.OPEN
  }

  public static send(message: string): void {
    if (Socket.isConnected()) {
      Socket._socket?.send(message)
    }
  }

  public static async callAction<T = any>(
    action: SocketAction,
    data: Record<string, unknown> | null = null
  ): Promise<T> {
    const deferredValue = deferred()
    const requestId = Math.random().toString(36).substring(7)
    const payload = {
      request_id: requestId,
      action,
      data
    }
    this.send(JSON.stringify(payload))
    this._deferredValues.set(requestId, deferredValue)

    const value = await Promise.race([
      deferredValue,
      new Promise((_, reject) =>
        setTimeout(() => {
          return reject('Timeout')
        }, 15_000)
      )
    ])
    this._deferredValues.delete(requestId)

    return value as T
  }
}

Socket.connect()
